import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AlertDialog } from '../ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';
import { apiService, ApiError } from '../../services/api';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Loader2,
  Crown,
  ExternalLink,
  CheckCircle,
  XCircle,
  Shield,
  ShieldCheck,
  BarChart3,
  Languages,
  Hash,
  Tag,
  Globe,
  Users,
  Trash2,
  MapPin
} from 'lucide-react';

export function UserProfile({ isActive }: { isActive?: boolean }) {
  const { user, refreshUser, logout } = useAuth();
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [isTogglingBureau, setIsTogglingBureau] = useState(false);
  const [showBureauConfirm, setShowBureauConfirm] = useState(false);
  const [pendingBureauValue, setPendingBureauValue] = useState(false);
  const [bureauError, setBureauError] = useState('');
  const [language, setLanguage] = useState<string>('en');
  const hasFetchedUserInfo = useRef(false);
  const [switchingPlanId, setSwitchingPlanId] = useState<'starter' | 'pro' | 'max' | null>(null);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annually'>('monthly');
  const singleBureauName = (() => {
    const provider = String(
      (user as { creditProvider?: string; provider?: string; crsUserId?: string | null } | null)?.creditProvider ||
      (user as { provider?: string } | null)?.provider ||
      ''
    ).toLowerCase();
    const isCRS = provider === 'crs' || !!(user as { crsUserId?: string | null } | null)?.crsUserId;
    return isCRS ? 'Equifax' : 'TransUnion';
  })();
  const streetAddress = (user as { streetAddress?: string } | null)?.streetAddress?.trim() || '';
  const city = (user as { city?: string } | null)?.city?.trim() || '';
  const state = (user as { state?: string } | null)?.state?.trim() || '';
  const zip = (user as { zip?: string } | null)?.zip?.trim() || '';
  const hasAddress = Boolean(streetAddress || city || state || zip);
  const cityStateZip = [city, state, zip].filter(Boolean).join(', ');

  // Fetch user info and update session storage on mount
  useEffect(() => {
    // Prevent duplicate requests
    if (hasFetchedUserInfo.current) return;
    hasFetchedUserInfo.current = true;

    let isMounted = true;

    const fetchUserInfo = async () => {
      if (!isMounted) return;
      try {
        await refreshUser();
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    };

    fetchUserInfo();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Background refresh when tab becomes active again (skip the initial activation)
  const hasActivatedOnce = useRef(false);
  useEffect(() => {
    if (!isActive) {
      hasActivatedOnce.current = true;
      return;
    }
    if (!hasActivatedOnce.current) {
      hasActivatedOnce.current = true;
      return;
    }

    const refreshInBackground = async () => {
      try {
        await refreshUser();
      } catch {
        // Silently fail — user still sees previous data
      }
    };
    refreshInBackground();
  }, [isActive, refreshUser]);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('appLanguage');
    if (savedLanguage) {
      setLanguage(savedLanguage);
      apiService.setLanguage(savedLanguage);
    }
  }, []);

  // Save language to localStorage and API service when it changes
  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    localStorage.setItem('appLanguage', newLanguage);
    apiService.setLanguage(newLanguage);
  };

  const handleDeleteUser = async () => {
    try {
      setIsDeleting(true);
      setDeleteError('');
      await apiService.deleteUser();
      logout(false);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete user';
      setDeleteError(msg);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };


  const generateIdentityIQLink = async () => {
    if (!user?.id) {
      console.error('User ID not available');
      return;
    }

    setIsGeneratingLink(true);
    try {
      const response = await apiService.generateIdentityIQLink(user.id);
      if (response.success) {
        window.open(response.link, '_blank');
      } else {
        console.error('Failed to generate IdentityIQ link:', response.message);
      }
    } catch (error) {
      console.error('Error generating IdentityIQ link:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleBureauToggleRequest = (checked: boolean) => {
    setPendingBureauValue(checked);
    setShowBureauConfirm(true);
  };

  const confirmBureauToggle = async () => {
    if (!user?.id) {
      setBureauError('User ID not available');
      return;
    }

    setIsTogglingBureau(true);
    setBureauError('');

    try {
      const response = await apiService.toggleMaxPlanAccess(user.id, pendingBureauValue);

      if (response.success) {
        // Refresh user data to get updated features
        await refreshUser();
      } else {
        setBureauError('Failed to update 3-bureau access');
      }
    } catch (error) {
      setBureauError(error instanceof ApiError ? error.message : 'Failed to update 3-bureau access');
    } finally {
      setIsTogglingBureau(false);
      setShowBureauConfirm(false);
    }
  };

  const getCurrentPlanId = (): 'starter' | 'pro' | 'max' => {
    const subscriptionType = (user as { subscriptionType?: string } | null)?.subscriptionType || '';
    if (subscriptionType.startsWith('FULL_ACCESS')) return 'max';
    if (subscriptionType.startsWith('PRO_ACCESS')) return 'pro';
    return 'starter';
  };

  const handlePlanSwitch = async (planId: 'starter' | 'pro' | 'max') => {
    try {
      setSwitchingPlanId(planId);
      setSubscriptionError('');
      const response = await apiService.createSubscriptionStripeInvoiceLink({ planId, billingPeriod });

      if (response?.success && response.checkoutUrl) {
        window.open(response.checkoutUrl, '_blank');
      } else {
        setSubscriptionError('Failed to generate checkout link. Please try again.');
      }
    } catch (error) {
      setSubscriptionError(error instanceof ApiError ? error.message : 'Failed to generate checkout link');
    } finally {
      setSwitchingPlanId(null);
    }
  };

  const currentPlanId = getCurrentPlanId();

  return (
    <div className="space-y-6">
      {/* Personal Information */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <User className="h-5 w-5 text-gray-700" />
          <h2 className="text-xl font-semibold text-gray-900">Personal Information</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">Main personal information</p>
        
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Name</p>
                <p className="text-base font-medium text-gray-900">{user?.firstName} {user?.lastName}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Email</p>
                <p className="text-base font-medium text-gray-900">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Enrollment Status</p>
                <p className={`text-base font-medium ${user?.enrolled ? 'text-green-600' : 'text-red-600'}`}>
                  {user?.enrolled ? 'Enrolled' : 'Not Enrolled'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Phone</p>
                <p className="text-base font-medium text-gray-900">{user?.phoneNumber}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Birth Date</p>
                <p className="text-base font-medium text-gray-900">
                  {user?.birthDate ? (() => {
                    if (user.birthDate.includes('-')) {
                      const [year, month, day] = user.birthDate.split('-');
                      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
                      return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
                    }
                    return user.birthDate;
                  })() : 'N/A'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Verification Status</p>
                <p className={`text-base font-medium ${user?.skippedVerification ? 'text-orange-600' : 'text-green-600'}`}>
                  {user?.skippedVerification ? 'Verification Skipped' : 'Verified'}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Member Since</p>
                <p className="text-base font-medium text-gray-900">
                  {user?.createdAt ? (() => {
                    const dateStr = user.createdAt.split('T')[0];
                    const [year, month, day] = dateStr.split('-');
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
                    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
                  })() : 'N/A'}
                </p>
              </div>
            </div>

            {user?.membershipNo && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Hash className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Membership No</p>
                  <p className="text-base font-medium text-gray-900">{user.membershipNo}</p>
                </div>
              </div>
            )}

            {(user?.offercode || user?.offerCode) && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Tag className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Offer Code</p>
                  <p className="text-base font-medium text-gray-900">{user.offercode || (user as any).offerCode}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Globe className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Source</p>
                <p className="text-base font-medium text-gray-900">{(user as any)?.cbSource || 'ios'}</p>
              </div>
            </div>

            {(user as any)?.referralId && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Referral ID</p>
                  <p className="text-base font-medium text-gray-900">{(user as any).referralId}</p>
                </div>
              </div>
            )}

            {hasAddress && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Address</p>
                  <p className="text-base font-medium text-gray-900">
                    {streetAddress}
                    {streetAddress && cityStateZip ? ', ' : ''}
                    {cityStateZip}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription Plan */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Crown className="h-5 w-5 text-gray-700" />
          <h2 className="text-xl font-semibold text-gray-900">Subscription Plan</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">Current subscription plan and features</p>

        <div className="border border-gray-200 rounded-lg px-4 py-3 mb-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900">Billing period</p>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${billingPeriod === 'monthly' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Monthly
              </span>
              <Switch
                checked={billingPeriod === 'annually'}
                onCheckedChange={(checked) => setBillingPeriod(checked ? 'annually' : 'monthly')}
                disabled={switchingPlanId !== null}
              />
              <span className={`text-sm ${billingPeriod === 'annually' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                Annual
              </span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Free Plan */}
          <div className="border border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Free Plan</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">$0</span>
              <span className="text-sm text-gray-500 ml-1">per month</span>
            </div>
            <Button 
              variant="outline"
              className="w-full mb-4 bg-gray-100 border-gray-300 text-gray-700"
              disabled={currentPlanId === 'starter' || switchingPlanId !== null}
              onClick={() => currentPlanId !== 'starter' && handlePlanSwitch('starter')}
            >
              {switchingPlanId === 'starter' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting...
                </>
              ) : currentPlanId === 'starter' ? 'Current Plan' : 'Switch to Starter'}
            </Button>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <span>Access to only 1 credit bureau - {singleBureauName}</span>
              </div>
            </div>
          </div>

          {/* Pro Plan */}
          <div className="border border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Pro Plan</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">$20</span>
              <span className="text-sm text-gray-500 ml-1">per month</span>
            </div>
            <Button 
              className="w-full mb-4 bg-black hover:bg-gray-800 text-white"
              disabled={currentPlanId === 'pro' || switchingPlanId !== null}
              onClick={() => currentPlanId !== 'pro' && handlePlanSwitch('pro')}
            >
              {switchingPlanId === 'pro' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting...
                </>
              ) : currentPlanId === 'pro' ? 'Current Plan' : 'Upgrade Plan'}
            </Button>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <span>Full access to all 3 credit bureaus</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <span>AI Credit Mentor</span>
              </div>
            </div>
          </div>

          {/* Max Plan */}
          <div className="border border-gray-200 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Max Plan</h3>
            <div className="mb-4">
              <span className="text-4xl font-bold text-gray-900">$100</span>
              <span className="text-sm text-gray-500 ml-1">per month</span>
            </div>
            <Button 
              className="w-full mb-4 bg-black hover:bg-gray-800 text-white"
              disabled={currentPlanId === 'max' || switchingPlanId !== null}
              onClick={() => currentPlanId !== 'max' && handlePlanSwitch('max')}
            >
              {switchingPlanId === 'max' ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Redirecting...
                </>
              ) : currentPlanId === 'max' ? 'Current Plan' : 'Upgrade Plan'}
            </Button>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <span>Full access to all 3 credit bureaus</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <span>AI Credit Mentor</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <CheckCircle className="h-4 w-4 text-gray-600 mt-0.5 flex-shrink-0" />
                <span>Automated AI Disputing</span>
              </div>
            </div>
          </div>
        </div>
        {subscriptionError && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{subscriptionError}</p>
          </div>
        )}
      </div>

      {/* Enable Max Mode Toggle */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-5 w-5 text-gray-700" />
          <h2 className="text-xl font-semibold text-gray-900">Enable Max Mode</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">Grant access to all features, unlimited</p>
        
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="font-medium text-gray-900">Max Mode Access</p>
                {user?.maxPlanEnabled ? (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                ) : (
                  <XCircle className="h-4 w-4 text-gray-400" />
                )}
              </div>
              <p className="text-sm text-gray-500">
                {user?.maxPlanEnabled
                  ? 'User has access to all features with unlimited usage'
                  : 'Currently limited to basic features'
                }
              </p>
            </div>
            <Switch
              checked={user?.maxPlanEnabled || false}
              onCheckedChange={handleBureauToggleRequest}
              disabled={isTogglingBureau}
            />
          </div>
        </div>

        {bureauError && (
          <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{bureauError}</p>
          </div>
        )}
      </div>

      {/* Language Preferences */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Languages className="h-5 w-5 text-gray-700" />
          <h2 className="text-xl font-semibold text-gray-900">Language Preferences</h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">Preferred language for the application</p>
        
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="font-medium text-gray-900 mb-1">User's Interface Language</p>
              <p className="text-sm text-gray-500">
                Choose the language for credit analytics, history, and marketplace recommendations
              </p>
            </div>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-[180px] ml-4">
                <SelectValue placeholder="Select language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="ua">Українська</SelectItem>
                <SelectItem value="ru">Русский</SelectItem>
                <SelectItem value="sr">Српски</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Credit Monitoring Portal */}
      {user?.enrolled && (user as any)?.provider === 'identityiq' && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-gray-700" />
            <h2 className="text-xl font-semibold text-gray-900">Credit Monitoring Portal</h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">Access detailed credit reports and monitoring dashboard</p>
          
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="font-medium text-gray-900 mb-1">IdentityIQ Portal</p>
                <p className="text-sm text-gray-500">
                  View your complete 3-bureau credit reports, monitor changes, and access detailed credit analysis
                </p>
              </div>
              <Button
                onClick={generateIdentityIQLink}
                disabled={isGeneratingLink || user?.skippedVerification}
                className="bg-black hover:bg-gray-800 text-white ml-4 flex items-center gap-2"
              >
                {isGeneratingLink ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4" />
                )}
                <span>{isGeneratingLink ? 'Generating...' : 'Open Portal'}</span>
              </Button>
            </div>
          </div>

          {user?.skippedVerification && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-md">
              <div className="flex items-start space-x-2">
                <Shield className="h-4 w-4 text-orange-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-orange-900">Verification Required</p>
                  <p className="text-sm text-orange-700 mt-1">
                    You need to complete identity verification before accessing the credit monitoring portal.
                    Please contact support to complete your verification process.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Account */}
      <div className="bg-white border border-red-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="h-5 w-5 text-red-600" />
          <h2 className="text-xl font-semibold text-gray-900">Delete Account</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Permanently delete this user account. This action cannot be undone.</p>

        {deleteError && (
          <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
            {deleteError}
          </div>
        )}

        <Button
          variant="destructive"
          onClick={() => setShowDeleteConfirm(true)}
          className="flex items-center gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Delete
        </Button>
      </div>

      {/* Bureau Confirmation Dialog */}
      <AlertDialog
        open={showBureauConfirm}
        onOpenChange={setShowBureauConfirm}
        title={pendingBureauValue ? 'Enable Max Plan?' : 'Disable Max Plan?'}
        description={
          pendingBureauValue
            ? 'You are about to enable Max Plan access to credit reports from all 3 bureaus (TransUnion, Experian, and Equifax). This will provide comprehensive credit analysis. Do you want to continue?'
            : `You are about to disable Max Plan access. You will only have access to ${singleBureauName} credit reports. Do you want to continue?`
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={confirmBureauToggle}
      />

      {/* Delete User Confirmation Dialog */}
      <AlertDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete User Account?"
        description="This will permanently delete this user account and all associated data. This action cannot be undone. Are you sure you want to proceed?"
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete Account'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteUser}
        variant="destructive"
      />

    </div>
  );
}
